import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Search, Filter, ChevronLeft, ChevronRight,
  CheckCircle2, Clock, XCircle, AlertCircle,
  Eye, Edit2, X, Save, Loader2, Download, Monitor, ArrowUpDown
} from "lucide-react";

import adminApi, { ADMIN_API } from "@/pages/admin/adminApi";

const fmt = (n) => new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS" }).format(n || 0);

const ESTADOS = [
  { value: "",           label: "Todos los estados" },
  { value: "approved",   label: "Aprobado" },
  { value: "acreditado", label: "Acreditado" },
  { value: "pending",    label: "Pendiente" },
  { value: "in_process", label: "En proceso" },
  { value: "rejected",   label: "Rechazado (MP)" },
  { value: "rechazado",  label: "Rechazado (manual)" },
  { value: "refunded",   label: "Devuelto" },
];

const ESTADO_BADGE = {
  approved:   "bg-green-100 text-green-700 border-green-200",
  acreditado: "bg-blue-100 text-blue-700 border-blue-200",
  pending:    "bg-yellow-100 text-yellow-700 border-yellow-200",
  in_process: "bg-sky-100 text-sky-700 border-sky-200",
  rejected:   "bg-red-100 text-red-700 border-red-200",
  rechazado:  "bg-red-100 text-red-700 border-red-200",
  refunded:   "bg-purple-100 text-purple-700 border-purple-200",
};

const STATUS_DETAIL_LABEL = {
  // Rechazos
  "cc_rejected_insufficient_amount":      "Saldo insuficiente",
  "cc_rejected_bad_filled_card_number":   "Número de tarjeta incorrecto",
  "cc_rejected_bad_filled_date":          "Fecha de vencimiento incorrecta",
  "cc_rejected_bad_filled_other":         "Datos de tarjeta incorrectos",
  "cc_rejected_bad_filled_security_code": "Código de seguridad incorrecto",
  "cc_rejected_blacklist":                "Tarjeta en lista negra",
  "cc_rejected_call_for_authorize":       "Requiere autorización del banco",
  "cc_rejected_card_disabled":            "Tarjeta deshabilitada",
  "cc_rejected_card_error":               "Error en la tarjeta",
  "cc_rejected_duplicated_payment":       "Pago duplicado",
  "cc_rejected_high_risk":                "Rechazado por riesgo",
  "cc_rejected_max_attempts":             "Máximo de intentos alcanzado",
  "cc_rejected_other_reason":             "Rechazado por el banco",
  "cc_amount_rate_limit_exceeded":        "Límite de monto superado",
  // Aprobados / estados
  "accredited":                           "Acreditado",
  "partially_refunded":                   "Devuelto parcialmente",
  "refunded":                             "Devuelto",
  "by_collector":                         "Devuelto por cobrador",
  "by_admin":                             "Devuelto por admin",
  "expired":                              "Expirado",
  "pending_contingency":                  "Pendiente por contingencia",
  "pending_review_manual":                "En revisión manual",
  "pending_waiting_payment":              "Esperando pago",
  "pending_waiting_transfer":             "Esperando transferencia",
  "bpp_refunded":                         "Devuelto",
  "offline_process":                      "Procesado offline",
  "in_process":                           "En proceso",
};

const METODO_PAGO_LABEL = {
  "account_money": "Dinero en cuenta MP",
  "visa":          "Visa",
  "master":        "Mastercard",
  "debvisa":       "Visa Débito",
  "debmaster":     "Mastercard Débito",
  "amex":          "American Express",
  "naranja":       "Naranja",
  "cabal":         "Cabal",
  "debcabal":      "Cabal Débito",
  "cencosud":      "Cencosud",
  "argencard":     "Argencard",
  "diners":        "Diners Club",
  "tarshop":       "Tarshop",
  "bank_transfer": "Transferencia bancaria",
  "atm":           "Cajero automático",
  "ticket":        "Pago en efectivo",
  "mercadopago":   "Mercado Pago",
};

const ESTADO_LABEL = {
  approved:   "Aprobado",
  acreditado: "Acreditado",
  pending:    "Pendiente",
  in_process: "En proceso",
  rejected:   "Rechazado",
  rechazado:  "Rechazado",
  refunded:   "Devuelto",
};

// ─── Celda Canal / Caja ───────────────────────────────────────────────────────
/**
 * Renderiza la columna "Caja / Lector" distinguiendo los 3 orígenes posibles:
 *   - POS físico con caja asignada   → nombre de la caja + últimos 8 del serial
 *   - POS físico sin caja asignada   → "📱 POS s/caja"  (ámbar)
 *   - QR estático del municipio      → "📷 QR Físico"   (violeta)
 *   - Portal web municipal           → "🌐 Web"         (gris)
 */
const CeldaCanal = ({ pago }) => {
  if (pago.caja_nombre) {
    return (
      <div className="flex items-start gap-1.5">
        <Monitor className="w-3.5 h-3.5 text-slate-400 mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-xs font-semibold text-slate-700 leading-tight">{pago.caja_nombre}</p>
          {pago.serial_lector && (
            <p className="text-xs text-slate-400 font-mono leading-tight">
              {pago.serial_lector.slice(-8)}
            </p>
          )}
        </div>
      </div>
    );
  }

  if (pago.canal === "pos_mp") {
    return (
      <span
        className="inline-flex items-center gap-1 text-xs text-amber-600 font-medium"
        title="Pago por lector POS físico sin caja asignada"
      >
        📱 POS s/caja
      </span>
    );
  }

  if (pago.canal === "qr_fisico") {
    return (
      <span
        className="inline-flex items-center gap-1 text-xs text-violet-500 font-medium"
        title="Pago por QR estático (cartelito físico del municipio) — no es del portal web"
      >
        📷 QR Físico
      </span>
    );
  }

  if (pago.canal === "transferencia") {
    return (
      <span
        className="inline-flex items-center gap-1 text-xs text-teal-600 font-medium"
        title="Transferencia bancaria directa a la cuenta MP del municipio"
      >
        🏦 Transferencia
      </span>
    );
  }

  // canal === "web" o cualquier otro valor
  return (
    <span className="inline-flex items-center gap-1 text-xs text-slate-400 font-medium">
      🌐 Web
    </span>
  );
};

// ─── Modal Detalle / Edición ──────────────────────────────────────────────────
const ModalPago = ({ pago, onClose, onGuardar, usuario }) => {
  const [nuevoEstado, setNuevoEstado] = useState(pago.estado);
  const [observacion, setObservacion] = useState(pago.observacion || "");
  const [guardando,   setGuardando]   = useState(false);
  const [detalle,     setDetalle]     = useState(null);
  const [loadingDetalle, setLoadingDetalle] = useState(true);

  const puedeEditar = usuario?.rol === "admin" || usuario?.rol === "tesoreria";

  // Un pago acreditado NO puede ser modificado por tesorería.
  // Solo admin puede re-abrirlo en casos excepcionales.
  const estaAcreditado           = pago.estado === "acreditado";
  const puedeModificarAcreditado = usuario?.rol === "admin";
  const bloqueado                = estaAcreditado && !puedeModificarAcreditado;

  useEffect(() => { cargarDetalle(); }, []);

  const cargarDetalle = async () => {
    try {
      const res = await adminApi.get(`${ADMIN_API}/pagos/${pago.payment_id}`);
      setDetalle(res.data);
    } catch {
      setDetalle(pago);
    } finally {
      setLoadingDetalle(false);
    }
  };

  const handleGuardar = async () => {
    setGuardando(true);
    try {
      await adminApi.put(`${ADMIN_API}/pagos/${pago.payment_id}/estado`, {
        estado:      nuevoEstado,
        observacion: observacion || null,
      });
      onGuardar();
    } catch (e) {
      alert(e.response?.data?.detail || "Error al guardar");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="font-bold text-slate-800 text-lg">Detalle del Pago</h2>
            <p className="text-slate-500 text-sm font-mono">{pago.payment_id}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-xl transition-colors"
          >
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Datos principales */}
          <div className="grid grid-cols-2 gap-4">
            {[
              { label: "Boleta(s)",      value: pago.boletas },
              { label: "Monto",          value: fmt(pago.monto) },
              { label: "Método de pago", value: pago.metodo_pago },
              { label: "Email pagador",  value: pago.pagador_email },
              { label: "Fecha de pago",  value: pago.fecha_pago?.replace("T", " ").substring(0, 19) },
              { label: "Actualizado por",value: pago.actualizado_por || "—" },
            ].map(({ label, value }) => (
              <div key={label} className="bg-slate-50 rounded-xl p-3">
                <p className="text-xs text-slate-400 font-medium mb-1">{label}</p>
                <p className="text-sm font-semibold text-slate-700 break-all">{value || "—"}</p>
              </div>
            ))}
          </div>

          {/* Canal de pago */}
          <div className="bg-slate-50 rounded-xl p-3">
            <p className="text-xs text-slate-400 font-medium mb-1">Canal de pago</p>
            <CeldaCanal pago={pago} />
          </div>

          {/* Estado actual */}
          <div>
            <p className="text-sm font-medium text-slate-600 mb-2">Estado actual</p>
            <span className={`inline-flex px-3 py-1 rounded-lg text-sm font-semibold border ${ESTADO_BADGE[pago.estado] || "bg-slate-100 text-slate-700 border-slate-200"}`}>
              {ESTADO_LABEL[pago.estado] || pago.estado}
            </span>
          </div>

          {/* Edición de estado — solo admin y tesorería */}
          {puedeEditar && (
            <div className="border-t pt-6 space-y-4">
              <p className="text-sm font-semibold text-slate-700">Actualizar estado</p>

              {/* Aviso: pago ya acreditado */}
              {estaAcreditado && (
                <div className={`flex items-start gap-3 rounded-xl px-4 py-3 border ${
                  puedeModificarAcreditado
                    ? "bg-amber-50 border-amber-200"
                    : "bg-green-50 border-green-200"
                }`}>
                  <CheckCircle2 className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                    puedeModificarAcreditado ? "text-amber-500" : "text-green-500"
                  }`} />
                  <div>
                    <p className={`text-sm font-semibold ${
                      puedeModificarAcreditado ? "text-amber-800" : "text-green-800"
                    }`}>
                      {puedeModificarAcreditado
                        ? "Este pago ya fue acreditado. Podés modificarlo solo en casos excepcionales."
                        : "Este pago ya fue acreditado por Tesorería. Las boletas asociadas fueron liberadas del estado pendiente."}
                    </p>
                    {!puedeModificarAcreditado && (
                      <p className="text-xs text-green-600 mt-1">
                        Solo un administrador puede revertir este estado.
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Aviso: cambio a acreditado implica liberar boletas */}
              {nuevoEstado === "acreditado" && pago.estado !== "acreditado" && (
                <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
                  <AlertCircle className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-blue-800">
                    Al acreditar este pago, las boletas{" "}
                    <span className="font-bold">{pago.boletas}</span> quedarán
                    registradas como saldadas y dejarán de aparecer como
                    "Pendiente Tesorería" en la consulta pública.
                  </p>
                </div>
              )}

              {!bloqueado && (
                <>
                  <div>
                    <label className="text-xs text-slate-500 font-medium mb-1 block">
                      Nuevo estado
                    </label>
                    <select
                      value={nuevoEstado}
                      onChange={e => setNuevoEstado(e.target.value)}
                      className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-blue-500 bg-white"
                    >
                      {ESTADOS.filter(e => e.value).map(e => (
                        <option key={e.value} value={e.value}>{e.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 font-medium mb-1 block">
                      Observación (opcional)
                    </label>
                    <textarea
                      value={observacion}
                      onChange={e => setObservacion(e.target.value)}
                      rows={3}
                      placeholder="Ej: Acreditado por expediente 1234/26..."
                      className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-blue-500 resize-none"
                    />
                  </div>
                  <button
                    onClick={handleGuardar}
                    disabled={guardando || nuevoEstado === pago.estado}
                    className={`w-full disabled:opacity-50 text-white font-semibold py-2.5 rounded-xl transition-colors flex items-center justify-center gap-2 ${
                      nuevoEstado === "acreditado" && pago.estado !== "acreditado"
                        ? "bg-green-600 hover:bg-green-700"
                        : "bg-blue-600 hover:bg-blue-700"
                    }`}
                  >
                    {guardando
                      ? <><Loader2 className="w-4 h-4 animate-spin" />Guardando...</>
                      : nuevoEstado === "acreditado" && pago.estado !== "acreditado"
                        ? <><CheckCircle2 className="w-4 h-4" />Confirmar Acreditación</>
                        : <><Save className="w-4 h-4" />Guardar cambios</>
                    }
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Componente principal ─────────────────────────────────────────────────────
const AdminPagos = ({ usuario }) => {
  const [pagos,  setPagos]  = useState([]);
  const [total,  setTotal]  = useState(0);
  const [page,   setPage]   = useState(1);
  const [pages,  setPages]  = useState(1);
  const [loading,setLoading]= useState(true);

  const [filtroEstado, setFiltroEstado] = useState("");
  const [filtroDesde,  setFiltroDesde]  = useState("");
  const [filtroHasta,  setFiltroHasta]  = useState("");
  const [buscar,       setBuscar]       = useState("");
  const [buscarInput,  setBuscarInput]  = useState("");

  const [modalPago, setModalPago] = useState(null);

  // ── Cajas POS ─────────────────────────────────────────────
  const [cajas,      setCajas]      = useState([]);
  const [filtroCaja, setFiltroCaja] = useState("");

  // ── Ordenamiento ──────────────────────────────────────────
  const [ordenCol, setOrdenCol] = useState("fecha_pago");
  const [ordenDir, setOrdenDir] = useState("desc");

  const cargar = useCallback(async (pg = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: pg, per_page: 20 });
      if (filtroEstado) params.set("estado",      filtroEstado);
      if (filtroDesde)  params.set("fecha_desde", filtroDesde);
      if (filtroHasta)  params.set("fecha_hasta", filtroHasta);
      if (buscar)       params.set("buscar",       buscar);
      if (filtroCaja)   params.set("caja_id",      filtroCaja);
      params.set("orden_col", ordenCol);
      params.set("orden_dir", ordenDir);

      const res = await adminApi.get(`${ADMIN_API}/pagos?${params}`);
      setPagos(res.data.pagos);
      setTotal(res.data.total);
      setPages(res.data.pages);
      setPage(pg);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [filtroEstado, filtroDesde, filtroHasta, buscar, filtroCaja, ordenCol, ordenDir]);

  useEffect(() => { cargar(1); }, [cargar]);

  useEffect(() => {
    const cargarCajas = async () => {
      try {
        const res = await adminApi.get(`${ADMIN_API}/cajas`);
        setCajas(Array.isArray(res.data) ? res.data : []);
      } catch { /* silencioso */ }
    };
    cargarCajas();
  }, []);

  const handleBuscar = (e) => {
    e.preventDefault();
    setBuscar(buscarInput);
  };

  const handleExportar = (canal = null) => {
    const token = localStorage.getItem("admin_token");
    if (!token) return;

    const params = new URLSearchParams({ _token: token });
    if (filtroEstado) params.set("estado",      filtroEstado);
    if (filtroDesde)  params.set("fecha_desde", filtroDesde);
    if (filtroHasta)  params.set("fecha_hasta", filtroHasta);
    if (buscar)       params.set("buscar",      buscar);
    if (canal)        params.set("canal",       canal);

    const url = `${process.env.REACT_APP_BACKEND_URL}/admin/api/pagos/exportar/csv?${params}`;
    window.open(url, "_blank");
  };

  return (
    <div className="space-y-6">
      {/* Encabezado */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Pagos</h1>
          <p className="text-slate-500 text-sm mt-1">{total} registros encontrados</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => handleExportar("web")}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2.5 rounded-xl transition-colors shadow-sm text-sm"
            title="Exportar solo pagos realizados desde la web"
          >
            <Download className="w-4 h-4" />
            Exportar Web
          </button>
          <button
            onClick={() => handleExportar("pos_mp")}
            className="flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white font-semibold px-4 py-2.5 rounded-xl transition-colors shadow-sm text-sm"
            title="Exportar pagos de POS (lectores físicos)"
          >
            <Download className="w-4 h-4" />
            Exportar POS
          </button>
          <button
            onClick={() => handleExportar("qr_fisico")}
            className="flex items-center gap-2 bg-violet-600 hover:bg-violet-700 text-white font-semibold px-4 py-2.5 rounded-xl transition-colors shadow-sm text-sm"
            title="Exportar pagos por QR estático (cartelito)"
          >
            <Download className="w-4 h-4" />
            Exportar QR
          </button>
          <button
            onClick={() => handleExportar(null)}
            className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white font-semibold px-4 py-2.5 rounded-xl transition-colors shadow-sm text-sm"
            title="Exportar todos los pagos"
          >
            <Download className="w-4 h-4" />
            Exportar Todo
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
        <div className="flex flex-wrap gap-3">
          {/* Buscador */}
          <form onSubmit={handleBuscar} className="flex gap-2 flex-1 min-w-48">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={buscarInput}
                onChange={e => setBuscarInput(e.target.value)}
                placeholder="Buscar por boleta, email, N° pago..."
                className="w-full border border-slate-200 rounded-xl pl-9 pr-4 py-2 text-sm focus:outline-none focus:border-blue-500"
              />
            </div>
            <button
              type="submit"
              className="bg-blue-600 text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-blue-700 transition-colors"
            >
              Buscar
            </button>
          </form>

          {/* Estado */}
          <select
            value={filtroEstado}
            onChange={e => setFiltroEstado(e.target.value)}
            className="border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-blue-500 bg-white"
          >
            {ESTADOS.map(e => (
              <option key={e.value} value={e.value}>{e.label}</option>
            ))}
          </select>

          {/* Caja POS */}
          {cajas.length > 0 && (
            <select
              value={filtroCaja}
              onChange={e => setFiltroCaja(e.target.value)}
              className="border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-blue-500 bg-white min-w-[160px]"
            >
              <option value="">Todas las cajas</option>
              {cajas.map(c => (
                <option key={c.id} value={c.id}>{c.nombre}</option>
              ))}
            </select>
          )}

          {/* Fechas */}
          <input
            type="date"
            value={filtroDesde}
            onChange={e => setFiltroDesde(e.target.value)}
            className="border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
          />
          <input
            type="date"
            value={filtroHasta}
            onChange={e => setFiltroHasta(e.target.value)}
            className="border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
          />

          {/* Limpiar filtros */}
          {(filtroEstado || filtroDesde || filtroHasta || buscar || filtroCaja) && (
            <button
              onClick={() => {
                setFiltroEstado("");
                setFiltroDesde("");
                setFiltroHasta("");
                setBuscar("");
                setBuscarInput("");
                setFiltroCaja("");
              }}
              className="flex items-center gap-1 text-slate-500 hover:text-red-500 text-sm px-3 py-2 border border-slate-200 rounded-xl transition-colors"
            >
              <X className="w-4 h-4" /> Limpiar
            </button>
          )}
        </div>
      </div>

      {/* Tabla */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
          </div>
        ) : pagos.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 text-slate-400">
            <AlertCircle className="w-8 h-8 mb-2" />
            <p>No se encontraron pagos</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr>
                  {[
                    { label: "N° Pago",      col: "payment_id"   },
                    { label: "Nomencla",      col: "nomencla"     },
                    { label: "Boleta(s)",     col: null           },
                    { label: "Monto",         col: "monto"        },
                    { label: "Estado",        col: "estado"       },
                    { label: "Detalle",       col: null           },
                    { label: "Medio de pago", col: null           },
                    { label: "Caja / Lector", col: "caja_id"      },
                    { label: "Email",         col: "pagador_email"},
                    { label: "Fecha",         col: "fecha_pago"   },
                    { label: "Acciones",      col: null           },
                  ].map(({ label, col }) => (
                    <th
                      key={label}
                      onClick={() => {
                        if (!col) return;
                        if (ordenCol === col) {
                          setOrdenDir(d => d === "asc" ? "desc" : "asc");
                        } else {
                          setOrdenCol(col);
                          setOrdenDir("desc");
                        }
                      }}
                      className={`px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase
                        ${col ? "cursor-pointer hover:text-slate-800 hover:bg-slate-100 select-none" : ""}`}
                    >
                      <span className="flex items-center gap-1">
                        {label}
                        {col && (
                          <ArrowUpDown className={`w-3 h-3 transition-colors
                            ${ordenCol === col ? "text-blue-500" : "text-slate-300"}`}
                          />
                        )}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {pagos.map((p) => (
                  <tr key={p.payment_id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3 text-xs font-mono text-slate-600">{p.payment_id}</td>
                    <td className="px-4 py-3 text-xs font-mono font-bold text-blue-700">
                      {p.nomencla || "—"}
                    </td>
                    <td className="px-4 py-3 max-w-32 truncate">
                      {p.boletas && (
                        p.boletas.startsWith("INSTORE") ||
                        p.boletas.toLowerCase() === "venta presencial"
                      ) ? (
                        <span className="text-xs text-slate-400 italic" title={p.boletas}>
                          Pago en ventanilla
                        </span>
                      ) : (
                        <span className="text-sm text-slate-700">{p.boletas}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm font-bold text-slate-800">
                      {fmt(p.monto)}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-1 rounded-lg text-xs font-semibold border ${
                        ESTADO_BADGE[p.estado] || "bg-slate-100 text-slate-700 border-slate-200"
                      }`}>
                        {ESTADO_LABEL[p.estado] || p.estado}
                      </span>
                    </td>
                    <td
                      className="px-4 py-3 text-xs text-slate-500 max-w-36 truncate"
                      title={STATUS_DETAIL_LABEL[p.status_detail] || p.status_detail}
                    >
                      {STATUS_DETAIL_LABEL[p.status_detail] || p.status_detail || "—"}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">
                      {METODO_PAGO_LABEL[p.metodo_pago] || p.metodo_pago || "—"}
                    </td>

                    {/* ── Columna canal / caja ── */}
                    <td className="px-4 py-3">
                      <CeldaCanal pago={p} />
                    </td>

                    <td className="px-4 py-3 text-xs text-slate-500 max-w-36 truncate">
                      {p.pagador_email}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">
                      {p.fecha_pago?.replace("T", " ").substring(0, 16)}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => setModalPago(p)}
                        className="flex items-center gap-1 text-blue-600 hover:text-blue-800 text-xs font-medium px-2 py-1 rounded-lg hover:bg-blue-50 transition-colors"
                      >
                        <Eye className="w-3.5 h-3.5" />
                        Ver
      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Paginación */}
        {pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
            <p className="text-xs text-slate-500">Página {page} de {pages}</p>
            <div className="flex gap-2">
              <button
                onClick={() => cargar(page - 1)}
                disabled={page === 1}
                className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-40 transition-colors"
              >
                <ChevronLeft className="w-4 h-4 text-slate-600" />
              </button>
              <button
                onClick={() => cargar(page + 1)}
                disabled={page === pages}
                className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-40 transition-colors"
              >
                <ChevronRight className="w-4 h-4 text-slate-600" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {modalPago && (
        <ModalPago
          pago={modalPago}
          usuario={usuario}
          onClose={() => setModalPago(null)}
          onGuardar={() => { setModalPago(null); cargar(page); }}
        />
      )}
    </div>
  );
};

export default AdminPagos;
